// Las tres cards por destino de la home del teléfono, en CARRUSEL
// (ADR-2026-10-12-los-destinos-en-carrusel-en-el-telefono): una por pantalla con la siguiente
// asomando, deslizables, y los puntos abajo. Apiladas ocupaban casi toda la primera pantalla.
// Pasa solo (ADR-2026-10-12-el-carrusel-de-destinos-pasa-solo): cada `CADA_MS`, en loop, con
// una pausa cuando la persona lo toca. Arranca por lo que la persona dijo en el onboarding que
// quería crear (ADR-2026-10-12-el-carrusel-arranca-por-lo-que-elegiste-en-el-onboarding).

import { useEffect, useRef, useState } from 'react';
import { CardDestinoMobile } from './CardDestinoMobile';
import { useShellMobile } from './shellMobile';
import { SECCIONES, type Seccion } from '../../estilos/CabeceraPorDestino';
import { supabase } from '../../../lib/supabase';
import { getCached, setCached } from '../../../utils/sessionCache';
import { empresaRecordada } from '../empresaDeLaSesion';
import { ordenDeDestinos } from './ordenDeDestinos';

/** En qué sección del sheet abre cada card (`null` = todavía no abre nada). */
const ABRE: Record<Seccion, 'redes' | 'catalogo' | null> = {
  redes: 'redes',
  catalogos: 'catalogo',
  anuncios: null,
};

const CLAVE_TIPOS = 'company:tipos-contenido';
const TTL_TIPOS_MS = 12 * 60 * 60 * 1000;

/**
 * `company_profiles.tipos_contenido`: lo que la persona eligió en el onboarding. Se cachea
 * por sesión (no cambia después del onboarding), así en las visitas siguientes la home ya
 * arranca en su card sin esperar la consulta. `null` mientras no llegó o si falla: el
 * carrusel queda en el orden de siempre.
 */
function useTiposDeContenido(): string[] | null {
  const [tipos, setTipos] = useState<string[] | null>(() => getCached<string[]>(CLAVE_TIPOS));
  useEffect(() => {
    if (tipos) return;
    let vigente = true;
    void (async () => {
      try {
        const { data: sesion } = await supabase.auth.getUser();
        const usuario = sesion.user?.id ?? null;
        if (!usuario) return;
        let idCompany = empresaRecordada();
        if (!idCompany) {
          const { data } = await supabase.from('profiles').select('id_company').eq('id', usuario).maybeSingle();
          idCompany = (data as { id_company: string | null } | null)?.id_company ?? null;
        }
        if (!idCompany) return;
        const { data } = await supabase
          .from('company_profiles')
          .select('tipos_contenido')
          .eq('id_company', idCompany)
          .maybeSingle();
        const lista = (data as { tipos_contenido: unknown } | null)?.tipos_contenido;
        const valor = Array.isArray(lista) ? lista.filter((x): x is string => typeof x === 'string') : [];
        setCached(CLAVE_TIPOS, valor, usuario, TTL_TIPOS_MS);
        if (vigente) setTipos(valor);
      } catch (e) {
        // Sin la respuesta, el orden de siempre: no vale romper la home por esto.
        console.warn('[CarruselDeDestinos] tipos_contenido', e);
      }
    })();
    return () => {
      vigente = false;
    };
  }, [tipos]);
  return tipos;
}

/** Cada cuánto pasa a la siguiente. */
const CADA_MS = 4500;
/** Cuánto se queda quieto después de que la persona lo toca (desliza o toca un punto). */
const PAUSA_AL_TOCAR_MS = 8000;

export function CarruselDeDestinos() {
  const { abrirSheet } = useShellMobile();
  const destinos = ordenDeDestinos(useTiposDeContenido()).map((seccion) => ({ seccion, abre: ABRE[seccion] }));
  const pistaRef = useRef<HTMLDivElement | null>(null);
  const [actual, setActual] = useState(0);
  const actualRef = useRef(0);
  actualRef.current = actual;
  /** Hasta cuándo no avanza solo (`Date.now()`): lo corre cada toque. */
  const quietoHastaRef = useRef(0);
  const tocar = () => {
    quietoHastaRef.current = Date.now() + PAUSA_AL_TOCAR_MS;
  };

  // La card a la vista es la que tiene el borde izquierdo más cerca del de la pista (el
  // snap es al inicio).
  const alDeslizar = () => {
    const pista = pistaRef.current;
    if (!pista) return;
    const slides = Array.from(pista.children) as HTMLElement[];
    const izquierda = pista.scrollLeft + pista.offsetLeft;
    let mejor = 0;
    slides.forEach((s, i) => {
      if (Math.abs(s.offsetLeft - izquierda) < Math.abs(slides[mejor].offsetLeft - izquierda)) mejor = i;
    });
    setActual(mejor);
  };

  const ir = (i: number) => {
    const pista = pistaRef.current;
    const slide = pista?.children[i] as HTMLElement | undefined;
    if (!pista || !slide) return;
    pista.scrollTo({ left: slide.offsetLeft - pista.offsetLeft, behavior: 'smooth' });
  };

  // Avanza solo, en loop. No con "reducir movimiento" (se mueve sólo si la persona lo
  // mueve), ni con la pestaña en segundo plano, ni durante la pausa después de un toque.
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(() => {
      if (document.hidden || Date.now() < quietoHastaRef.current) return;
      ir((actualRef.current + 1) % destinos.length);
    }, CADA_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div>
      {/* `-mx-5 px-5 scroll-px-5`: la pista llega a los bordes de la pantalla (las cards se
          deslizan por debajo del margen) y el snap respeta la sangría de 20px de la home. */}
      <div
        ref={pistaRef}
        onScroll={alDeslizar}
        onPointerDown={tocar}
        onTouchStart={tocar}
        className="scrollbar-hide -mx-5 flex snap-x snap-mandatory gap-2.5 overflow-x-auto scroll-px-5 px-5"
      >
        {destinos.map(({ seccion, abre }) => (
          // 86%: la siguiente asoma y se entiende que hay más. `flex` para que las tres midan
          // lo que la más alta (Catálogos tiene una línea más).
          <div key={seccion} className="flex w-[86%] shrink-0 snap-start">
            <CardDestinoMobile
              seccion={seccion}
              // 0.82 y corrido 84 px: el borde de la card corta la segunda foto por la mitad
              // (su centro está a 187 de las 290 unidades del abanico → 103 unidades afuera).
              unidadAbanico="0.82px"
              abanicoAfuera={84}
              onClick={() => abre && abrirSheet?.(abre)}
            />
          </div>
        ))}
      </div>
      <div className="mt-3 flex justify-center gap-1.5" role="tablist" aria-label="Destinos">
        {destinos.map(({ seccion }, i) => (
          <button
            key={seccion}
            type="button"
            role="tab"
            aria-selected={i === actual}
            aria-label={SECCIONES[seccion].titulo}
            onClick={() => {
              tocar();
              ir(i);
            }}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === actual ? 'w-5 bg-[#16181D]/70' : 'w-1.5 bg-[#16181D]/20'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
